/**
 * Per-module field catalogues + import endpoints for the shared
 * GuidedImportWizard. Every module ships its own config here so the wizard
 * stays 100% config-driven and one bug-fix propagates everywhere.
 *
 * Field shape:
 *   { key, label, required, type, enum, pattern, patternHint, min, max,
 *     unique, aliases (extra strings to match source headers), example }
 */

// ── Engineering DCI ──────────────────────────────────────────────────
export const ENGINEERING_DCI_CONFIG = (projectId) => ({
  moduleLabel: 'Engineering DCI',
  templateEndpoint: `/projects/${projectId}/engineering-schedule/entries/template`,
  importEndpoint:   `/projects/${projectId}/engineering-schedule/entries/bulk-import`,
  fields: [
    { key: 'document_no', label: 'Document No', required: true, unique: true, aliases: ['doc_no','document_number','drawing_no'], example: 'MEC-PID-0001' },
    { key: 'title',       label: 'Title',       required: true, aliases: ['description','document_title'], example: 'P&ID — Utility Water System' },
    { key: 'discipline',  label: 'Discipline',  required: true, enum: ['Mechanical','Civil','Structural','Electrical','Instrumentation','Piping','Process','HVAC','Fire & Safety'], aliases: ['disc'] },
    { key: 'document_type', label: 'Document Type', required: false, enum: ['P&ID','GA','ISO','Datasheet','Specification','Calculation','MTO','Report','Study'], aliases: ['doc_type','type'] },
    { key: 'revision',    label: 'Revision',    required: false, pattern: '^[A-Z]?[0-9]{0,2}$', patternHint: 'e.g. A, B1, 0, 1', example: 'A' },
    { key: 'planned_start',  label: 'Planned Start',  type: 'date', aliases: ['plan_start','planned_start_date'] },
    { key: 'planned_finish', label: 'Planned Finish', type: 'date', aliases: ['plan_finish','planned_finish_date'] },
    { key: 'actual_start',   label: 'Actual Start',   type: 'date', aliases: ['act_start'] },
    { key: 'actual_finish',  label: 'Actual Finish',  type: 'date', aliases: ['act_finish'] },
    { key: 'progress_pct',   label: 'Progress %',     type: 'number', min: 0, max: 100, aliases: ['progress','percent_complete'] },
    { key: 'status',         label: 'Status',         enum: ['NOT_STARTED','IN_PROGRESS','IFA_ISSUED','IFC_ISSUED','SUPERSEDED'], aliases: ['stage'] },
    { key: 'weight',         label: 'Weight',         type: 'number', min: 0, max: 100 },
  ],
  sampleRows: [{
    'Document No': 'MEC-PID-0001', 'Title': 'P&ID — Utility Water System',
    'Discipline': 'Mechanical', 'Document Type': 'P&ID', 'Revision': 'A',
    'Planned Start': '2026-01-05', 'Planned Finish': '2026-03-15',
    'Actual Start': '2026-01-10', 'Actual Finish': '', 'Progress %': 45,
    'Status': 'IN_PROGRESS', 'Weight': 3.5,
  }],
});

// ── Procurement MCI ──────────────────────────────────────────────────
export const PROCUREMENT_MCI_CONFIG = (projectId) => ({
  moduleLabel: 'Procurement MCI',
  templateEndpoint: `/v1/projects/${projectId}/mci/entries/template`,
  importEndpoint:   `/v1/projects/${projectId}/mci/entries/bulk-import`,
  exportEndpoint:   `/v1/projects/${projectId}/mci/entries/export`,
  exportLabel:      'Download Current Register',
  extraTemplates: [
    {
      label: 'Stage-Gate Matrix (BULK + TAGGED)',
      endpoint: `/v1/projects/${projectId}/mci/entries/stage-gate-matrix`,
      description: 'MCI × stage-gate matrix — one row per gate. Edit Planned / Forecast / Actual dates in Excel and re-upload here to bulk-patch dates across BULK and TAGGED items.',
    },
  ],
  matrixImports: [
    {
      label: 'Upload Stage-Gate Matrix',
      importEndpoint: `/v1/projects/${projectId}/mci/entries/stage-gate-matrix/import`,
      description: 'Upload the filled Stage-Gate matrix. You will see a cell-level diff (before → after) BEFORE anything is saved.',
    },
  ],
  fields: [
    { key: 'materialCode', label: 'Material Code', required: true, unique: true, aliases: ['mat_code','matcode','item_code'], example: 'MEC-STEEL-001' },
    { key: 'description',  label: 'Description',   required: true, aliases: ['desc','item_description'], example: 'Structural steel plate' },
    { key: 'materialType', label: 'Material Type', required: true, enum: ['BULK','TAGGED','LONG_LEAD','COMMODITY'], aliases: ['type','category'] },
    { key: 'unit',         label: 'Unit',          required: false, enum: ['EA','KG','TON','MT','M','M2','M3','L','SET','LOT'], aliases: ['uom','unit_of_measure'] },
    { key: 'quantity',     label: 'Quantity',      type: 'number', min: 0, aliases: ['qty'] },
    { key: 'po_number',    label: 'PO Number',     aliases: ['po_no','purchase_order'], example: 'PO-2026-0142' },
    { key: 'vendor',       label: 'Vendor',        aliases: ['supplier'] },
    { key: 'expected_delivery', label: 'Expected Delivery', type: 'date', aliases: ['delivery_date','edd'] },
    { key: 'actual_delivery',   label: 'Actual Delivery',   type: 'date' },
    { key: 'status',            label: 'Status',            enum: ['REQUISITIONED','RFQ','PO_ISSUED','IN_TRANSIT','DELIVERED','QC_HOLD','ACCEPTED'], aliases: ['stage'] },
  ],
  sampleRows: [{
    'Material Code': 'MEC-STEEL-001', 'Description': 'Structural steel plate 12mm',
    'Material Type': 'BULK', 'Unit': 'TON', 'Quantity': 240, 'PO Number': 'PO-2026-0142',
    'Vendor': 'TATA Steel', 'Expected Delivery': '2026-04-30',
    'Actual Delivery': '', 'Status': 'PO_ISSUED',
  }],
});

// ── Fabrication & Construction (F&C activities) ──────────────────────
export const FABRICATION_CONSTRUCTION_CONFIG = (projectId) => ({
  moduleLabel: 'Fabrication & Construction',
  templateEndpoint: `/v1/projects/${projectId}/fc/activities/template`,
  importEndpoint:   `/v1/projects/${projectId}/fc/activities/bulk-import`,
  fields: [
    { key: 'activityCode', label: 'Activity Code', required: true, unique: true, aliases: ['act_code','activity_id'], example: 'FAB-P-0001' },
    { key: 'name',         label: 'Activity Name', required: true, aliases: ['activity_name','description'], example: 'Fabricate spool SP-101' },
    { key: 'phase',        label: 'Phase',         required: true, enum: ['FABRICATION','CONSTRUCTION','ERECTION','WELDING','TESTING'], aliases: ['stage'] },
    { key: 'discipline',   label: 'Discipline',    enum: ['Piping','Structural','Mechanical','Electrical','Instrumentation','Civil','HVAC'] },
    { key: 'workfront',    label: 'Workfront',     aliases: ['wf','area'], example: 'Unit-100 · Level 1' },
    { key: 'planned_start',  label: 'Planned Start',  type: 'date', required: true },
    { key: 'planned_finish', label: 'Planned Finish', type: 'date', required: true },
    { key: 'actual_start',   label: 'Actual Start',   type: 'date' },
    { key: 'actual_finish',  label: 'Actual Finish',  type: 'date' },
    { key: 'progress_pct',   label: 'Progress %',     type: 'number', min: 0, max: 100 },
    { key: 'weight',         label: 'Weight',         type: 'number', min: 0, max: 100 },
    { key: 'quantity',       label: 'Quantity',       type: 'number', min: 0 },
    { key: 'unit',           label: 'Unit',           enum: ['EA','KG','MT','M','INCH-DIA','WELD','JOINT','LOT','M2','M3'] },
  ],
  sampleRows: [{
    'Activity Code': 'FAB-P-0001', 'Activity Name': 'Fabricate spool SP-101',
    'Phase': 'FABRICATION', 'Discipline': 'Piping', 'Workfront': 'Unit-100 · Level 1',
    'Planned Start': '2026-05-01', 'Planned Finish': '2026-05-15',
    'Actual Start': '', 'Actual Finish': '', 'Progress %': 0,
    'Weight': 0.8, 'Quantity': 12, 'Unit': 'INCH-DIA',
  }],
});

// ── Pre-Commissioning (PCCM) ─────────────────────────────────────────
export const PRECOMMISSIONING_CONFIG = (projectId) => ({
  moduleLabel: 'Pre-Commissioning',
  templateEndpoint: `/v1/projects/${projectId}/pccm/entries/template`,
  importEndpoint:   `/v1/projects/${projectId}/pccm/entries/bulk-import`,
  fields: [
    { key: 'itrCode',    label: 'ITR Code',    required: true, unique: true, aliases: ['itr_no','pretest_id'], example: 'ITR-A-0001' },
    { key: 'itrType',    label: 'ITR Type',    required: true, enum: ['A','B','C','MC-Cert'], patternHint: 'A/B/C only' },
    { key: 'systemNo',   label: 'System No',   required: true, aliases: ['sys_no','subsystem'], example: 'SYS-201' },
    { key: 'description', label: 'Description', required: true },
    { key: 'discipline', label: 'Discipline',  enum: ['Piping','Electrical','Instrumentation','Mechanical','Civil','HVAC'] },
    { key: 'planned_date', label: 'Planned Date', type: 'date', required: true },
    { key: 'actual_date',  label: 'Actual Date',  type: 'date' },
    { key: 'status',       label: 'Status',       enum: ['NOT_STARTED','IN_PROGRESS','PUNCHED','COMPLETED','MC_ACCEPTED'], aliases: ['stage'] },
    { key: 'punch_category', label: 'Punch Category', enum: ['A','B','C',''], required: false, patternHint: 'A/B/C (blank if no punch)' },
  ],
  sampleRows: [{
    'ITR Code': 'ITR-A-0001', 'ITR Type': 'A', 'System No': 'SYS-201',
    'Description': 'Line-check completion — Water System', 'Discipline': 'Piping',
    'Planned Date': '2027-01-15', 'Actual Date': '', 'Status': 'NOT_STARTED', 'Punch Category': '',
  }],
});

// ── Commissioning (COMM) ─────────────────────────────────────────────
export const COMMISSIONING_CONFIG = (projectId) => ({
  moduleLabel: 'Commissioning',
  templateEndpoint: `/v1/projects/${projectId}/commissioning/entries/template`,
  importEndpoint:   `/v1/projects/${projectId}/commissioning/entries/bulk-import`,
  fields: [
    { key: 'systemNo',   label: 'System No',    required: true, unique: true, aliases: ['sys_no','subsystem'], example: 'SYS-201' },
    { key: 'description', label: 'Description', required: true },
    { key: 'discipline', label: 'Discipline',   enum: ['Piping','Electrical','Instrumentation','Mechanical','Process','Utilities'] },
    { key: 'commissioningPhase', label: 'Commissioning Phase', required: true, enum: ['LOOP_CHECK','FUNCTIONAL_TEST','ENERGISATION','START_UP','PERFORMANCE_TEST','HAND_OVER'], aliases: ['phase','stage'] },
    { key: 'planned_start',  label: 'Planned Start',  type: 'date', required: true },
    { key: 'planned_finish', label: 'Planned Finish', type: 'date', required: true },
    { key: 'actual_start',   label: 'Actual Start',   type: 'date' },
    { key: 'actual_finish',  label: 'Actual Finish',  type: 'date' },
    { key: 'progress_pct',   label: 'Progress %',     type: 'number', min: 0, max: 100 },
    { key: 'status',         label: 'Status',         enum: ['NOT_STARTED','IN_PROGRESS','HELD','COMPLETED','ACCEPTED'] },
    { key: 'rfsu_status',    label: 'RFSU Status',    enum: ['','PENDING','ISSUED','ACCEPTED'] },
  ],
  sampleRows: [{
    'System No': 'SYS-201', 'Description': 'Utility water start-up',
    'Discipline': 'Piping', 'Commissioning Phase': 'START_UP',
    'Planned Start': '2027-04-01', 'Planned Finish': '2027-04-15',
    'Actual Start': '', 'Actual Finish': '', 'Progress %': 0,
    'Status': 'NOT_STARTED', 'RFSU Status': '',
  }],
});
